import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReassignKamService } from '@app/region-management/services/reassign-kam.service';
import { ActiveKamUser } from '@app/shared/models/user';
import { RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { UserService } from '@app/shared/services/api/user.service';
import { isNil, orderBy, uniqBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';

export class KamErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'app-reassign-kam-dialog',
  templateUrl: './reassign-kam-dialog.component.html',
  styleUrls: ['./reassign-kam-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReassignKamDialogComponent {
  newKamControl = new FormControl('', [Validators.required, this.isValidKam.bind(this)]);
  municipalities: any[] = [];
  municipality;
  primaryKams: any[] = [];
  filteredKams: Observable<ActiveKamUser[]> = of();
  municipalityRegionMapping;
  kamErrorMatcher = new KamErrorStateMatcher();
  isMoveAll = false;
  isLoading = false;
  showCheckBox = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private reassignKam: ReassignKamService,
    private userService: UserService,
    private regionMunicipalities: RegionMunicipalitiesService,
    private dialogRef: MatDialogRef<ReassignKamDialogComponent>
  ) {
    this.municipality = data;
    if (data.currKamGuid) {
      this.isLoading = true;
      this.loadMunicipalitiesForKam(data.currKamGuid)
        .pipe(catchError(() => of([])))
        .subscribe((assignments) => {
          if (isNil(this.municipality.municipalityGuid)) {
            this.showCheckBox = false;
            this.selectAll(true);
            this.isMoveAll = true;
          } else {
            this.showCheckBox = assignments.length > 1;
          }
          this.isLoading = false;
        });
    } else {
      this.showCheckBox = false;
    }

    this.userService
      .getActiveKamUsers()
      .pipe(map((x) => x.filter((k) => k.guid !== data.currKamGuid)))
      .subscribe((x) => {
        this.filteredKams = this.newKamControl.valueChanges.pipe(
          startWith(''),
          map((k) => this.filterKam(k || '', x))
        );
      });

    this.municipalityRegionMapping = this.regionMunicipalities.municipalityRegionMapping$;
  }

  loadMunicipalitiesForKam = (guid: string) =>
    this.reassignKam.getMunicipalities(this.municipality.currKamGuid).pipe(tap((x: any[]) => (this.municipalities = orderBy(x, ['name'], ['asc']))));

  filterKam = (searchTerm: string, kams: ActiveKamUser[] = []) =>
    kams.filter((kam) => kam.name.toString().toLowerCase().indexOf(searchTerm.toString().toLowerCase()) === 0);

  selectAll = (selected: any) => {
    this.municipalities.forEach((m) => (m.selected = selected));
  };

  displayKam = (kam: any) => (kam && kam.name ? kam.name : '');

  reassignMunicipalitiesToKam = (selectedMunicipalities: any, defaultMunicipality: any, newKam: any) => {
    this.isLoading = true;
    if (defaultMunicipality.municipalityGuid) {
      selectedMunicipalities.push({
        guid: defaultMunicipality.municipalityGuid,
        ...defaultMunicipality,
      });
    }
    this.reassignKam
      .reassignMunicipalitiesToKam(this.getSelectedMunicipalitiesKeys(selectedMunicipalities) as [], newKam)
      .pipe(catchError(this.reassignError))
      .subscribe(() => {
        this.isLoading = false;
        this.dialogRef.close(true);
      });
  };

  reassignError = (error: any) => {
    this.dialogRef.close(false);
    return error;
  };

  private getSelectedMunicipalitiesKeys = (selectedMunicipalities: []) => uniqBy(selectedMunicipalities, 'guid');

  isValidKam(control: AbstractControl): ValidationErrors | null {
    return (control.dirty || control.touched) && typeof control.value === 'string' ? { invalid: true } : null;
  }

  get selectedMunicipalities() {
    return this.municipalities.filter((municipality) => municipality.selected);
  }

  cancel = () => this.dialogRef.close();
}
